import { useState } from 'react';
import { UMButton, UMIcon } from '../../urbanmap/_components';
import { MyRatiosCard } from './MyRatiosCard';
import { ProjectRatiosCard } from './ProjectRatiosCard';
import { PC, UMR } from './tokens';
import type { DegreeDays, RatioTable as Ratios, Referential, WeatherStation } from './types';

interface Props {
  referential: Referential;
  /** Référentiel recalé sur ses coefficients de performance. */
  defaultRatios: Ratios;
  /** Station choisie à l'étape précédente. */
  station: WeatherStation;
  /** Degrés-jours retenus pour cette station (standards ou corrigés). */
  stationDegrees: DegreeDays;
  onStationDegreesChange: (next: DegreeDays) => void;
  /** Degrés-jours de mes ratios (saisis), et mes ratios. */
  myDegrees: DegreeDays;
  myRatios: Ratios;
  /** Degrés-jours de mes ratios tels qu'appliqués au projet. */
  appliedDegrees: DegreeDays;
  defaultDegrees: DegreeDays;
  onMyDegreesChange: (next: DegreeDays) => void;
  onMyRatiosChange: (next: Ratios) => void;
  /** Mes ratios calibrés sur la station, et le référentiel calibré sur les mêmes degrés-jours. */
  projectRatios: Ratios;
  stationRatios: Ratios;
  pending: boolean;
  /** Mes ratios ont déjà été appliqués au moins une fois. */
  applied: boolean;
  onApply: () => void;
  onRatiosValidity: (ok: boolean) => void;
  onEditingChange: (editing: boolean) => void;
  actions: React.ReactNode;
}

/** Étape 2 (obligatoire) : mes ratios, puis leur application au projet via la station météo. */
export function StepSettings(p: Props) {
  const [myOk, setMyOk] = useState(true);
  const [stationOk, setStationOk] = useState(true);
  const [editing, setEditing] = useState(false);
  const [stationEditing, setStationEditing] = useState(false);

  const emit = (mine: boolean, station: boolean) => {
    setMyOk(mine); setStationOk(station); p.onRatiosValidity(mine && station);
  };
  const blocked = editing || !myOk;
  const disabled = blocked || !p.pending;

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <MyRatiosCard
          degrees={p.myDegrees}
          ratios={p.myRatios}
          defaults={p.defaultRatios}
          defaultDegrees={p.defaultDegrees}
          onDegreesChange={p.onMyDegreesChange}
          onRatiosChange={p.onMyRatiosChange}
          onValidityChange={(ok) => emit(ok, stationOk)}
          onEditingChange={(on) => { setEditing(on); p.onEditingChange(on || stationEditing); }}
        />

        {/* Passerelle entre les deux blocs : c'est elle qui transfère mes ratios au projet. */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px',
            background: !disabled ? PC.accentBg : UMR.white,
            border: `1px dashed ${!disabled ? PC.accentBorder : UMR.border}`,
          }}
        >
          <span style={{ width: 22, height: 22, flex: '0 0 auto', background: UMR.white, border: `1px solid ${UMR.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>2</span>
          <UMButton icon="published_with_changes" disabled={disabled} onClick={p.onApply}>
            Appliquer mes ratios à mon projet — {p.station.name}
          </UMButton>
          <UMIcon name={editing ? 'edit' : p.pending ? 'arrow_downward' : 'check_circle'} size={20} color={blocked ? UMR.textSecondary : p.pending ? PC.accentStrong : PC.accent} />
          <span style={{ fontSize: 13, flex: 1, minWidth: 220 }}>
            {editing
              ? 'Validez d’abord vos ratios et coefficients pour pouvoir les appliquer à votre projet.'
              : p.pending
                ? `Mes ratios ont changé : appliquez-les à mon projet pour les calibrer sur ${p.station.name}.`
                : `Mes ratios sont appliqués à mon projet, calibrés sur ${p.station.name}.`}
          </span>
        </div>

        <ProjectRatiosCard
          station={p.station}
          degrees={p.stationDegrees}
          onDegreesChange={p.onStationDegreesChange}
          onValidityChange={(ok) => emit(myOk, ok)}
          onEditingChange={(on) => { setStationEditing(on); p.onEditingChange(editing || on); }}
          ratios={p.projectRatios}
          appliedDegrees={p.appliedDegrees}
          applied={p.applied}
        />
        {p.actions}
      </div>
    </div>
  );
}
